"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */
import { c as _c } from "react/compiler-runtime";
import { Area, Bar, CartesianGrid, Label, Line, ComposedChart as RechartsComposedChart, Legend as RechartsLegend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { AxisDomain } from "recharts/types/util/types";
import React from "react";
import { AvailableChartColors, type AvailableChartColorsKeys, constructCategoryColors, getColorClassName, getYAxisDomain } from "@/lib/chart-utils";
import { cx } from "@/lib/tailwind-utils";

//#region Tooltip

interface ChartTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
  leftFormatter: (value: number) => string;
  rightFormatter: (value: number) => string;
  rightCategories: Set<string>;
}
const ChartTooltip = t0 => {
  const $ = _c(16);
  const {
    active,
    payload,
    label,
    leftFormatter,
    rightFormatter,
    rightCategories
  } = t0;
  if (active && payload && payload.length) {
    let t1;
    if ($[0] !== label) {
      t1 = <div className="border-b border-inherit px-4 py-2"><p className="font-medium text-foreground">{label}</p></div>;
      $[0] = label;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== leftFormatter || $[3] !== payload || $[4] !== rightCategories || $[5] !== rightFormatter) {
      let t3;
      if ($[7] !== leftFormatter || $[8] !== rightCategories || $[9] !== rightFormatter) {
        t3 = (item, index) => <div key={`id-${index}`} className="flex items-center justify-between space-x-8"><div className="flex items-center space-x-2"><span aria-hidden="true" className="size-2 rounded-xs shrink-0" style={{
              backgroundColor: item.color
            }} /><p className="whitespace-nowrap text-right text-foreground-secondary">{item.name}</p></div><p className="whitespace-nowrap text-right font-medium tabular-nums text-foreground">{rightCategories.has(item.name) ? rightFormatter(item.value) : leftFormatter(item.value)}</p></div>;
        $[7] = leftFormatter;
        $[8] = rightCategories;
        $[9] = rightFormatter;
        $[10] = t3;
      } else {
        t3 = $[10];
      }
      t2 = payload.map(t3);
      $[2] = leftFormatter;
      $[3] = payload;
      $[4] = rightCategories;
      $[5] = rightFormatter;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[11] !== t2) {
      t3 = <div className="space-y-1 px-4 py-2">{t2}</div>;
      $[11] = t2;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    let t4;
    if ($[13] !== t1 || $[14] !== t3) {
      t4 = <div className="rounded-md border border-border bg-background/80 text-body shadow-md backdrop-blur-2xl">{t1}{t3}</div>;
      $[13] = t1;
      $[14] = t3;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    return t4;
  }
  return null;
};

//#region ComposedChart

export interface ComposedChartProps extends React.HTMLAttributes<HTMLDivElement> {
  data: Record<string, any>[];
  index: string;
  /**
   * Categories to render as bars (plotted on left Y axis).
   * If omitted, all categories not in lineCategories or areaCategories are bars.
   */
  barCategories?: string[];
  /**
   * Categories to render as lines (plotted on right Y axis when bar categories
   * are present, allowing different scales).
   */
  lineCategories?: string[];
  /** Categories to render as filled areas (plotted on left Y axis). */
  areaCategories?: string[];
  colors?: AvailableChartColorsKeys[];
  valueFormatter?: (value: number) => string;
  /** Separate formatter for right-axis (line) series */
  rightValueFormatter?: (value: number) => string;
  showXAxis?: boolean;
  showYAxis?: boolean;
  showGridLines?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  yAxisWidth?: number;
  xAxisLabel?: string;
  yAxisLabel?: string;
  rightYAxisLabel?: string;
  autoMinValue?: boolean;
  minValue?: number;
  maxValue?: number;
  allowDecimals?: boolean;
  animated?: boolean;
}
const PALETTE: Record<string, string> = {
  blue: "#3b82f6",
  emerald: "#10b981",
  violet: "#8b5cf6",
  amber: "#f59e0b",
  cyan: "#06b6d4",
  pink: "#ec4899",
  lime: "#84cc16",
  fuchsia: "#d946ef",
  gray: "#6b7280"
};
const ComposedChart = React.forwardRef<HTMLDivElement, ComposedChartProps>((props, forwardedRef) => {
  const {
    data = [],
    index,
    barCategories,
    lineCategories = [],
    areaCategories = [],
    colors = AvailableChartColors,
    valueFormatter = v => v.toLocaleString(),
    rightValueFormatter,
    showXAxis = true,
    showYAxis = true,
    showGridLines = true,
    showLegend = true,
    showTooltip = true,
    yAxisWidth = 56,
    xAxisLabel,
    yAxisLabel,
    rightYAxisLabel,
    autoMinValue = false,
    minValue,
    maxValue,
    allowDecimals = true,
    className,
    animated = true,
    ...other
  } = props;

  // Derive effective bar categories: explicit prop, or everything not in lines/areas
  const effectiveBarCategories = barCategories ?? (() => {
    const reserved = new Set([...lineCategories, ...areaCategories]);
    if (data.length > 0) {
      return Object.keys(data[0]).filter(k => k !== index && !reserved.has(k));
    }
    return [];
  })();

  // All unique categories across all series types
  const allCategories = Array.from(new Set([...effectiveBarCategories, ...lineCategories, ...areaCategories]));
  const categoryColors = constructCategoryColors(allCategories, colors);
  const yAxisDomain = getYAxisDomain(autoMinValue, minValue, maxValue);
  const getColor = (cat: string): string => {
    const colorKey = categoryColors.get(cat) as AvailableChartColorsKeys;
    return PALETTE[colorKey] ?? "#3b82f6";
  };

  // Use dual Y axes when we have both bars and lines (different scales)
  const hasDualAxes = effectiveBarCategories.length > 0 && lineCategories.length > 0;
  const rightCategoriesSet = new Set(lineCategories);
  return <div ref={forwardedRef} className={cx("h-80 w-full", className)} tremor-id="tremor-raw" {...other}>
        <ResponsiveContainer>
          <RechartsComposedChart data={data} margin={{
        bottom: xAxisLabel ? 30 : undefined,
        left: yAxisLabel ? 20 : undefined,
        right: hasDualAxes ? rightYAxisLabel ? 60 : 50 : 5,
        top: yAxisLabel || rightYAxisLabel ? 15 : 5
      }}>
            {showGridLines && <CartesianGrid className="stroke-border stroke-1" horizontal vertical={false} />}
            <XAxis hide={!showXAxis} dataKey={index} tick={{
          className: "text-caption fill-muted-foreground",
          transform: "translate(0, 6)"
        }} fill="" stroke="" tickLine={false} axisLine={false} minTickGap={5}>
              {xAxisLabel && <Label position="insideBottom" offset={-20} className="fill-foreground text-body-strong">
                  {xAxisLabel}
                </Label>}
            </XAxis>

            {/* Left Y axis — bars and areas */}
            <YAxis yAxisId="left" width={yAxisWidth} hide={!showYAxis} axisLine={false} tickLine={false} fill="" stroke="" tick={{
          className: "text-caption fill-muted-foreground",
          transform: "translate(-3, 0)"
        }} type="number" domain={yAxisDomain as AxisDomain} tickFormatter={valueFormatter} allowDecimals={allowDecimals}>
              {yAxisLabel && <Label position="insideLeft" style={{
            textAnchor: "middle"
          }} angle={-90} offset={-15} className="fill-foreground text-body-strong">
                  {yAxisLabel}
                </Label>}
            </YAxis>

            {/* Right Y axis — lines (only when dual axes needed) */}
            {hasDualAxes && <YAxis yAxisId="right" orientation="right" width={yAxisWidth} hide={!showYAxis} axisLine={false} tickLine={false} fill="" stroke="" tick={{
          className: "text-caption fill-muted-foreground",
          transform: "translate(3, 0)"
        }} type="number" tickFormatter={rightValueFormatter ?? valueFormatter} allowDecimals={allowDecimals}>
                {rightYAxisLabel && <Label position="insideRight" style={{
            textAnchor: "middle"
          }} angle={90} offset={15} className="fill-foreground text-body-strong">
                    {rightYAxisLabel}
                  </Label>}
              </YAxis>}

            {showTooltip && <Tooltip wrapperStyle={{
          outline: "none",
          zIndex: 10
        }} isAnimationActive animationDuration={100} cursor={{
          fill: "var(--border)",
          opacity: 0.15
        }} content={({
          active,
          payload,
          label
        }) => <ChartTooltip active={active} payload={payload ? [...payload] : []} label={label === undefined ? undefined : String(label)} leftFormatter={valueFormatter} rightFormatter={rightValueFormatter ?? valueFormatter} rightCategories={rightCategoriesSet} />} />}
            {showLegend && <RechartsLegend verticalAlign="top" wrapperStyle={{
          paddingBottom: 12,
          color: "var(--foreground)"
        }} />}

            {effectiveBarCategories.map(cat_0 => <Bar key={cat_0} yAxisId="left" dataKey={cat_0} fill={getColor(cat_0)} className={cx(getColorClassName(categoryColors.get(cat_0) as AvailableChartColorsKeys, "fill"))} isAnimationActive={animated} animationDuration={animated ? 600 : 0} />)}
            {areaCategories.map(cat_1 => <Area key={cat_1} yAxisId="left" type="monotone" dataKey={cat_1} stroke={getColor(cat_1)} fill={getColor(cat_1)} fillOpacity={0.2} className={cx(getColorClassName(categoryColors.get(cat_1) as AvailableChartColorsKeys, "stroke"))} isAnimationActive={animated} animationDuration={animated ? 600 : 0} />)}
            {lineCategories.map(cat_2 => <Line key={cat_2} yAxisId={hasDualAxes ? "right" : "left"} type="monotone" dataKey={cat_2} stroke={getColor(cat_2)} strokeWidth={2} dot={{
          r: 3,
          fill: getColor(cat_2)
        }} activeDot={{
          r: 5
        }} className={cx(getColorClassName(categoryColors.get(cat_2) as AvailableChartColorsKeys, "stroke"))} isAnimationActive={animated} animationDuration={animated ? 600 : 0} />)}
          </RechartsComposedChart>
        </ResponsiveContainer>
      </div>;
});
ComposedChart.displayName = "ComposedChart";
export { ComposedChart };